import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Rating,
  Alert,
  LinearProgress,
  Container,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Business,
  Phone,
  Email,
  LocationOn,
  Receipt,
  CalendarToday,
  AttachMoney,
  TrendingUp,
  Schedule,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { styled } from '@mui/material/styles';

// Styled components for better customization
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 8px 32px 0 rgba(0,0,0,0.15)',
    transform: 'translateY(-2px)',
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  boxShadow: '0 2px 12px 0 rgba(0,0,0,0.1)',
  padding: theme.spacing(1),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  fontSize: '2.5rem',
  fontWeight: 'bold',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 4px 20px 0 rgba(102, 126, 234, 0.3)',
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  borderRadius: 8,
  padding: '4px 8px',
  minWidth: 80,
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

export const InvSupplierDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchSupplierDetails();
  }, [id]);

  const fetchSupplierDetails = async () => {
    try {
      const [supplierResponse, performanceResponse] = await Promise.all([
        suppliersAPI.getById(id),
        suppliersAPI.getPerformance(id),
      ]);

      setSupplier(supplierResponse.data.data.supplier);
      setPerformance(performanceResponse.data.data.performance);
      setPurchaseOrders(supplierResponse.data.data.recentOrders || []);
    } catch (error) {
      console.error('Error fetching supplier details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getInitials = (name) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  };

  const getPaymentTermsColor = (terms) => {
    const colors = {
      advance: 'success',
      '7_days': 'info',
      '15_days': 'primary',
      '30_days': 'warning',
      '45_days': 'warning',
      '60_days': 'error',
    };
    return colors[terms] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      cancelled: 'error',
      pending: 'warning',
      approved: 'info',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2, fontSize: '1.1rem', mt: 2 }}>
        Supplier not found
      </Alert>
    );
  }

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
          variant="outlined"
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1,
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          Back to Suppliers
        </Button>
        <Typography 
          variant="h3" 
          fontWeight="bold" 
          sx={{ 
            flexGrow: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          Supplier Details
        </Typography>
        <Button
          startIcon={<Edit />}
          variant="contained"
          onClick={() => navigate(`/hospital/hospital-inventory/suppliers/${id}/edit`)}
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          Edit Supplier
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Supplier Information */}
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <StyledAvatar sx={{ mx: 'auto', mb: 3 }}>
                {getInitials(supplier.name)}
              </StyledAvatar>
              
              <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                {supplier.name}
              </Typography>
              
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {supplier.contactPerson}
              </Typography>

              <Rating 
                value={supplier.rating} 
                readOnly 
                size="large"
                sx={{ mb: 3 }} 
              />

              <StatusChip
                label={supplier.isActive ? 'Active' : 'Inactive'}
                color={supplier.isActive ? 'success' : 'default'}
                sx={{ mb: 3, fontSize: '1rem' }}
              />

              {/* Contact Information */}
              <Box sx={{ textAlign: 'left', mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                  <Phone fontSize="large" color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                      Primary Phone
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {supplier.phone}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                  <Email fontSize="large" color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                      Email Address
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {supplier.email}
                    </Typography>
                  </Box>
                </Box>
                {supplier.alternatePhone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Phone fontSize="large" color="secondary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontSize="0.9rem">
                        Alternate Phone
                      </Typography>
                      <Typography variant="h6" fontWeight="600">
                        {supplier.alternatePhone}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>

          {/* Business Information */}
          <StyledCard sx={{ mt: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="primary" />
                Business Information
              </Typography>
              
              <Box sx={{ space: 2 }}>
                {supplier.gstNumber && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" color="text.secondary" fontSize="1rem">
                      GST Number
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {supplier.gstNumber}
                    </Typography>
                  </Box>
                )}
                {supplier.panNumber && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" color="text.secondary" fontSize="1rem">
                      PAN Number
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {supplier.panNumber}
                    </Typography>
                  </Box>
                )}
                {supplier.drugLicenseNumber && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" color="text.secondary" fontSize="1rem">
                      Drug License
                    </Typography>
                    <Typography variant="h6" fontWeight="600">
                      {supplier.drugLicenseNumber}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" color="text.secondary" fontSize="1rem">
                    Payment Terms
                  </Typography>
                  <Chip
                    label={supplier.paymentTerms.replace('_', ' ')}
                    color={getPaymentTermsColor(supplier.paymentTerms)}
                    size="medium"
                    sx={{ fontSize: '1rem', fontWeight: 600, mt: 1 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body1" color="text.secondary" fontSize="1rem">
                    Lead Time
                  </Typography>
                  <Typography variant="h6" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Schedule color="primary" />
                    {supplier.leadTime} days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </StyledCard>

          {/* Supplier Items */}
          {supplier.items && supplier.items.length > 0 && (
            <StyledCard sx={{ mt: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                  Supplied Items
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {supplier.items.map((supplierItem, index) => (
                    <Card 
                      key={supplierItem._id} 
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        p: 2.5, 
                        borderRadius: 3,
                        border: '2px solid',
                        borderColor: 'divider',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {supplierItem.item?.name || 'Item'}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        <Box component="span" fontWeight="600" color="primary.main">
                          ₹{supplierItem.costPrice}
                        </Box>
                        {' | '}
                        Min Qty: {supplierItem.minOrderQuantity}
                      </Typography>
                      {supplierItem.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>
                          {supplierItem.notes}
                        </Typography>
                      )}
                      <Chip
                        label={supplierItem.isActive ? 'Active' : 'Inactive'}
                        color={supplierItem.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Card>
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          )}
        </Grid>

        {/* Performance & Orders */}
        <Grid item xs={12} md={8}>
          <StyledCard>
            <CardContent sx={{ p: 0 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    py: 3,
                    px: 4,
                    minWidth: 'auto',
                  }
                }}
              >
                <Tab label="Performance" />
                <Tab label="Purchase Orders" />
                <Tab label="Address" />
              </Tabs>

              {/* Performance Tab */}
              <TabPanel value={tabValue} index={0}>
                {performance ? (
                  <Box>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <StatCard>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <CalendarToday sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h3" fontWeight="bold" color="primary.main">
                              {performance.totalOrders}
                            </Typography>
                            <Typography variant="h6" color="text.secondary" fontWeight="600">
                              Total Orders
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StatCard>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <AttachMoney sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                            <Typography variant="h3" fontWeight="bold" color="success.main">
                              ₹{performance.totalSpent.toLocaleString()}
                            </Typography>
                            <Typography variant="h6" color="text.secondary" fontWeight="600">
                              Total Spent
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StatCard>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                            <Typography variant="h3" fontWeight="bold" color="warning.main">
                              ₹{performance.averageOrderValue.toFixed(2)}
                            </Typography>
                            <Typography variant="h6" color="text.secondary" fontWeight="600">
                              Avg. Order Value
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <StatCard>
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <Schedule sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                            <Typography variant="h3" fontWeight="bold" color="info.main">
                              {supplier.leadTime}
                            </Typography>
                            <Typography variant="h6" color="text.secondary" fontWeight="600">
                              Lead Time (Days)
                            </Typography>
                          </CardContent>
                        </StatCard>
                      </Grid>
                    </Grid>

                    {/* Category Spending */}
                    {performance.categorySpending && Object.keys(performance.categorySpending).length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                          Spending by Category
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                          <Table>
                            <TableHead sx={{ backgroundColor: 'action.hover' }}>
                              <TableRow>
                                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Category</TableCell>
                                <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Amount Spent</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(performance.categorySpending).map(([category, amount]) => (
                                <TableRow key={category} hover>
                                  <TableCell sx={{ py: 2 }}>
                                    <Typography variant="h6" fontWeight="600">
                                      {category || 'Uncategorized'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 2 }}>
                                    <Typography variant="h6" color="success.main" fontWeight="600">
                                      ₹{amount.toLocaleString()}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {/* Monthly Spending */}
                    {performance.monthlySpending && performance.monthlySpending.length > 0 && (
                      <Box>
                        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                          Monthly Spending
                        </Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                          <Table>
                            <TableHead sx={{ backgroundColor: 'action.hover' }}>
                              <TableRow>
                                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Month</TableCell>
                                <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Amount Spent</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {performance.monthlySpending.map((monthData) => (
                                <TableRow key={monthData.month} hover>
                                  <TableCell sx={{ py: 2 }}>
                                    <Typography variant="h6" fontWeight="600">
                                      {monthData.month}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right" sx={{ py: 2 }}>
                                    <Typography variant="h6" color="success.main" fontWeight="600">
                                      ₹{monthData.amount.toLocaleString()}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary">
                      No performance data available
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Purchase Orders Tab */}
              <TabPanel value={tabValue} index={1}>
                {purchaseOrders.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table>
                      <TableHead sx={{ backgroundColor: 'action.hover' }}>
                        <TableRow>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>PO Number</TableCell>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Order Date</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Amount</TableCell>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Status</TableCell>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600, py: 2 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {purchaseOrders.map((order) => (
                          <TableRow key={order._id} hover>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="600">
                                {order.poNumber}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="h6">
                                {new Date(order.orderDate).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 2 }}>
                              <Typography variant="h6" fontWeight="600" color="primary.main">
                                ₹{order.grandTotal?.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <StatusChip
                                label={order.status.replace('_', ' ')}
                                color={getStatusColor(order.status)}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Button
                                size="large"
                                startIcon={<Receipt />}
                                onClick={() => navigate(`/purchases/${order._id}`)}
                                variant="outlined"
                                sx={{
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  fontSize: '1rem',
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary">
                      No purchase orders found
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Address Tab */}
              <TabPanel value={tabValue} index={2}>
                {supplier.address ? (
                  <StyledCard>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                        <LocationOn sx={{ fontSize: 40, color: 'primary.main', mt: 0.5 }} />
                        <Box>
                          <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Registered Address
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 2 }}>
                            {supplier.address.street}
                          </Typography>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            {supplier.address.city}, {supplier.address.state}
                          </Typography>
                          <Typography variant="h6" color="text.secondary">
                            {supplier.address.zipCode}, {supplier.address.country}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </StyledCard>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary">
                      No address information available
                    </Typography>
                  </Box>
                )}
              </TabPanel>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Container>
    </Box>
  );
};